import { CriarConta } from './CriarConta';
import { notifications } from '@mantine/notifications';
import { IconEye, IconEyeOff, IconLock, IconMail } from '@tabler/icons-react';
import { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { ApiRequestError, registerApi, useBrand, useSession, slugDaPorta } from '@eva/domain';
import { AuthFrame } from './AuthFrame';
import { destinoDepoisDoLogin } from '../app/guards';
import { aplicarEmpresaPendente } from '../cadastro/entrar';
import { BotaoPrincipal, CampoDeTexto } from '../cadastro/novo/kit';

export function LoginPage() {
  const session = useSession();
  const { brand, detection } = useBrand();
  const navigate = useNavigate();
  const [enviando, setEnviando] = useState(false);

  // Quem descobriu no cadastro que já tinha conta chega com o e-mail (A062).
  const [email, setEmail] = useState(() => new URLSearchParams(window.location.search).get('email') ?? '');
  const [password, setPassword] = useState('');
  const [verSenha, setVerSenha] = useState(false);
  const [tentou, setTentou] = useState(false);
  const erroDoEmail = /^\S+@\S+\.\S+$/.test(email.trim()) ? undefined : 'E-mail inválido';
  const erroDaSenha = password ? undefined : 'Informe a senha';

  if (session.status === 'ready' && session.context) return <Navigate to={destinoDepoisDoLogin(session.context.role)} replace />;
  if (session.status === 'choosing') return <Navigate to="/contextos" replace />;

  const entrar = async () => {
    setTentou(true);
    if (erroDoEmail || erroDaSenha) return;
    setEnviando(true);
    try {
      // A porta: nenhum slug na base (é assim que a mantenedora entra), o slug
      // da marca resolvida nas outras — subdomínio, caminho ou domínio (A050).
      const decisao = await session.login(email.trim().toLowerCase(), password, slugDaPorta(brand, detection));
      if (decisao.kind === 'direct') {
        // Link da empresa guardado do cadastro (A062): aplicado depois do login.
        if (decisao.context.role === 'PATIENT') await aplicarEmpresaPendente();
        navigate(destinoDepoisDoLogin(decisao.context.role), { replace: true });
      } else if (decisao.kind === 'choose') {
        navigate('/contextos', { replace: true });
      } else {
        notifications.show({ color: 'red', title: 'Sem acesso', message: 'Esta conta não tem vínculo com nenhuma marca.' });
        await session.logout();
      }
    } catch (e) {
      if (e instanceof ApiRequestError && /email (not verified|não verificado)/i.test(e.message)) {
        // Conta criada sem confirmar o e-mail: reenvia o código e leva à confirmação.
        const em = email.trim().toLowerCase();
        await registerApi.sendEmailVerification(em).catch(() => undefined);
        navigate(`/cadastro/createPasswordOTP?email=${encodeURIComponent(em)}`);
        return;
      }
      // Conta sem vínculo na marca deste endereço recebe o mesmo 401 de senha
      // errada (A069): até 06/10/2026 a API apontava a marca da paciente e o
      // app redirecionava para lá.
      const msg = e instanceof ApiRequestError ? traduzir(e) : 'Ocorreu um erro inesperado. Tente novamente.';
      notifications.show({ color: 'red', title: 'Não foi possível entrar', message: msg });
    } finally {
      setEnviando(false);
    }
  };

  // O mesmo desenho do cadastro (A082): moldura com o degradê da marca,
  // campos arredondados com ícone e o botão principal do kit.
  return (
    <AuthFrame title="Bem-vinda de volta!" subtitle="é muito bom tê-la conosco!">
      <form
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void entrar();
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 20 }}
      >
        <CampoDeTexto
          id="entrar-email"
          rotulo="E-mail"
          icone={<IconMail size={20} />}
          valor={email}
          onChange={setEmail}
          tipo="email"
          inputMode="email"
          placeholder="seu@email.com"
          autoComplete="email"
          erro={tentou ? erroDoEmail : undefined}
        />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <CampoDeTexto
            id="entrar-senha"
            rotulo="Senha"
            icone={<IconLock size={20} />}
            valor={password}
            onChange={setPassword}
            tipo={verSenha ? 'text' : 'password'}
            placeholder="Sua senha"
            autoComplete="current-password"
            erro={tentou ? erroDaSenha : undefined}
            direita={
              <button type="button" onClick={() => setVerSenha((v) => !v)} aria-label={verSenha ? 'Esconder senha' : 'Mostrar senha'} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex', color: '#808080' }}>
                {verSenha ? <IconEyeOff size={20} /> : <IconEye size={20} />}
              </button>
            }
          />
          <Link to="/esqueci-senha" style={{ alignSelf: 'flex-end', fontSize: 14, fontWeight: 600, color: 'var(--eva-brand-accent-text)', textDecoration: 'none' }}>
            Esqueci minha senha
          </Link>
        </div>
        <BotaoPrincipal tipo="submit" loading={enviando}>Entrar</BotaoPrincipal>
      </form>
      <CriarConta />
    </AuthFrame>
  );
}

function traduzir(e: ApiRequestError): string {
  if (e.status === 401) return 'Email ou senha incorretos.';
  if (e.status === 403) return e.message || 'Acesso não permitido.';
  if (e.status === 0) return 'Sem conexão com o servidor.';
  return e.message;
}
